import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AddActivityPayload,
  AddCollaboratorPayload,
  AdmissionEnquiry,
  AdmissionFunnelResponse,
  AdmissionListResponse,
  AdmissionRecord,
  AdvanceEnquiryPayload,
  AdvanceIfCompleteResponse,
  AISuggestionResponse,
  AllowNextStagePayload,
  AllowNextStageResponse,
  AssignEnquiryPayload,
  AttemptListResponse,
  BulkAdvancePayload,
  BulkAssignPayload,
  BulkDeletePayload,
  BulkResultResponse,
  CalendarResponse,
  CheatAssessment,
  CheatAssessmentRecomputeRequest,
  ConvertEnquiryPayload,
  CreateEnquiryPayload,
  EnquiryListFilters,
  EnquiryListResponse,
  ForceAdvancePayload,
  ForceAdvanceResponse,
  GrantReattemptRequest,
  GrantReattemptResponse,
  RejectEnquiryPayload,
  ReviewProctorArtifactKind,
  ReviewProctorArtifactList,
  ReviewProctorDownloadUrl,
  ReviewProctorEventKind,
  ReviewProctorEventList,
  SetFollowupPayload,
  StaffRosterResponse,
  StageStatusResponse,
  UpdateEnquiryPayload,
  WorkloadResponse,
} from "@eduviaos/shared";
import { ApiError } from "@eduviaos/shared";
import { api } from "@/lib/api";

/**
 * TanStack Query bindings for the admission HTTP surface.
 *
 * Phase 1a-WS3-B  — base CRUD + advance + funnel + active-form
 * Phase B2.6b     — runtime stage→form wiring (active-form, stage-payload)
 * Phase B2.6c-now — convert (enquiry → Admission record)
 * Phase D1a       — reject + soft-delete + restore + source/q filters,
 *                   plus stage_label resolution on active-form
 *
 * The BE auto-derives the active vertical from the bearer's Tenant
 * document when no `X-Vertical` header is sent, so we deliberately
 * omit it here — keeps the FE decoupled from the per-tenant funnel
 * choice. (See `app/domains/admission/routes.py::_vertical`.)
 *
 * Local re-exports (kept for backwards-compat with the existing
 * `EnquiryDetailSheet` import path) — new callers should import the
 * shared types from `@eduviaos/shared`.
 */
export type StageTransition = AdmissionEnquiry["stage_history"][number];
export type Enquiry = AdmissionEnquiry;
export type { EnquiryListResponse };

// ── Active-form types (still local — they're FE-only B2.6b shapes) ─────────

export interface ActiveFormFieldDTO {
  key: string;
  label: string;
  field_type: string;
  order: number;
  description: string | null;
  placeholder: string | null;
  default_value: unknown;
  validation: Record<string, unknown>;
  options: Array<{ value: string; label: string }> | null;
  conditional_visibility: Record<string, unknown> | null;
  conditional_required: Record<string, unknown> | null;
}

export interface ActiveFormDTO {
  slug: string;
  name: string;
  purpose: string;
  purpose_ref_id: string | null;
  version: number;
  submit_button_label: string;
  success_message: string;
  requires_auth: boolean;
  fields: ActiveFormFieldDTO[];
}

export interface ActiveFormResponse {
  enquiry_id: string;
  stage_key: string;
  stage_label: string | null;
  form: ActiveFormDTO | null;
  saved_payload: Record<string, unknown> | null;
  saved_payload_meta: {
    saved_at: string;
    saved_by: string | null;
    form_slug: string;
    form_version: number;
  } | null;
}

export interface StagePayloadResponse {
  enquiry: AdmissionEnquiry;
  stage_key: string;
}

// ── Query-key factory (single source of truth for cache invalidation) ─────

export const admissionKeys = {
  all: ["admissions"] as const,
  funnel: (branchId?: string | null) =>
    [...admissionKeys.all, "funnel", branchId ?? "_root"] as const,
  enquiries: (filters?: EnquiryListFilters) =>
    [...admissionKeys.all, "enquiries", filters ?? {}] as const,
  enquiry: (id: string | null) => [...admissionKeys.all, "enquiry", id] as const,
  activeForm: (id: string | null) => [...admissionKeys.all, "active-form", id] as const,
  admissions: (params?: { status?: string; branch_id?: string }) =>
    [...admissionKeys.all, "converted", params ?? {}] as const,
  admission: (id: string | null) => [...admissionKeys.all, "converted", id] as const,
  // Phase D3-D6
  workload: () => [...admissionKeys.all, "workload"] as const,
  calendar: (params?: { window_days?: number; include_overdue?: boolean }) =>
    [...admissionKeys.all, "calendar", params ?? {}] as const,
  staff: () => [...admissionKeys.all, "staff"] as const,
  // Phase E2b — AI counsellor suggestion (per-enquiry, on-demand)
  aiSuggestion: (id: string | null) => [...admissionKeys.all, "ai-suggestion", id] as const,
  // Phase F0 — action-gated stage engine (per-enquiry, polled while open)
  stageStatus: (id: string | null) => [...admissionKeys.all, "stage-status", id] as const,
};

// ── Funnel (drives stage order + labels everywhere) ───────────────────────

export function useFunnel(params?: { branch_id?: string }) {
  const search = new URLSearchParams();
  if (params?.branch_id) search.set("branch_id", params.branch_id);
  const qs = search.toString();
  return useQuery<AdmissionFunnelResponse>({
    queryKey: admissionKeys.funnel(params?.branch_id ?? null),
    queryFn: () => api.get<AdmissionFunnelResponse>(`/admissions/funnel${qs ? `?${qs}` : ""}`),
    // Funnel config is SA-driven and rarely changes within a session,
    // so a 60s stale window keeps the table/board lookups instant.
    staleTime: 60_000,
  });
}

// ── List + read ───────────────────────────────────────────────────────────

function buildListQuery(filters?: EnquiryListFilters): string {
  const search = new URLSearchParams();
  if (!filters) return "";
  if (filters.stage_key) search.set("stage_key", filters.stage_key);
  if (filters.assigned_to) search.set("assigned_to", filters.assigned_to);
  if (filters.branch_id) search.set("branch_id", filters.branch_id);
  if (filters.source) search.set("source", filters.source);
  if (filters.q) search.set("q", filters.q);
  if (filters.include_deleted) search.set("include_deleted", "true");
  if (filters.only_deleted) search.set("only_deleted", "true");
  if (filters.followup_filter) search.set("followup_filter", filters.followup_filter);
  if (filters.limit !== undefined) search.set("limit", String(filters.limit));
  if (filters.offset !== undefined) search.set("offset", String(filters.offset));
  return search.toString();
}

export function useEnquiries(filters?: EnquiryListFilters) {
  const qs = buildListQuery(filters);
  return useQuery<EnquiryListResponse>({
    queryKey: admissionKeys.enquiries(filters),
    queryFn: () => api.get<EnquiryListResponse>(`/admissions/enquiries${qs ? `?${qs}` : ""}`),
    // Operational data — short stale window keeps the table fresh
    // when the IA flips back from a different tab.
    staleTime: 15_000,
  });
}

export function useEnquiry(enquiryId: string | null) {
  return useQuery<AdmissionEnquiry>({
    queryKey: admissionKeys.enquiry(enquiryId),
    queryFn: () => api.get<AdmissionEnquiry>(`/admissions/enquiries/${enquiryId}`),
    enabled: Boolean(enquiryId),
    staleTime: 15_000,
  });
}

// ── B2.6b: active form lookup ─────────────────────────────────────────────

export function useActiveForm(enquiryId: string | null) {
  return useQuery<ActiveFormResponse>({
    queryKey: admissionKeys.activeForm(enquiryId),
    queryFn: () => api.get<ActiveFormResponse>(`/admissions/enquiries/${enquiryId}/active-form`),
    enabled: Boolean(enquiryId),
    staleTime: 30_000,
  });
}

// ── Phase E2b: AI counsellor suggestion ────────────────────────────────────

/**
 * Fetch the AI-generated counsellor recommendation for an enquiry.
 *
 * Lazy by default (`enabled: false` until the IA explicitly clicks
 * "Get AI suggestion") — AI calls cost money and the chip should NEVER
 * fire on page mount. The chip component flips `enabled` to true on
 * click and the query takes over.
 *
 * Stale window: 5 minutes. The BE produces a snapshot suggestion based
 * on current enquiry state; if the IA edits notes / changes stage, the
 * detail page already invalidates `admissionKeys.enquiry(id)` so the
 * surrounding data updates — but the SUGGESTION text itself is locked
 * to the moment of generation. The chip surfaces `generated_at` so the
 * IA knows whether to refetch before acting.
 *
 * `retry: false` — we never auto-retry an AI 503. The chain itself
 * already retries within Gemini → NIM. A second client-side burst
 * would just hammer the breakers; the chip exposes a manual "Try
 * again" button for the user to opt back in.
 */
export function useAiSuggestion(enquiryId: string | null, enabled: boolean) {
  return useQuery<AISuggestionResponse, ApiError>({
    queryKey: admissionKeys.aiSuggestion(enquiryId),
    queryFn: () =>
      api.get<AISuggestionResponse>(`/admissions/enquiries/${enquiryId}/ai-suggestion`),
    enabled: Boolean(enquiryId) && enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
    gcTime: 30 * 60 * 1000,
  });
}

// ── Phase F0: action-gated stage engine ────────────────────────────────────

/**
 * `GET /admissions/enquiries/{id}/stage-status` — pure read-model.
 *
 * Drives the StageProgressPanel chips and the "Advance now" CTA on the
 * enquiry-detail page. We poll on a soft cadence (15s) WHILE the user
 * has the page open so a stage can flip to `complete` without a manual
 * refresh — common case: parent fills the apply form via the public
 * portal in another tab; counsellor sees the chip flip green within
 * one polling cycle and the auto-advance CTA lights up.
 *
 * Cache key is per-enquiry; the cross-cutting `admissionKeys.all`
 * invalidation triggered by every other mutation in this file pulls a
 * fresh status without us having to thread invalidations everywhere.
 */
export function useStageStatus(enquiryId: string | null) {
  return useQuery<StageStatusResponse>({
    queryKey: admissionKeys.stageStatus(enquiryId),
    queryFn: () => api.get<StageStatusResponse>(`/admissions/enquiries/${enquiryId}/stage-status`),
    enabled: Boolean(enquiryId),
    staleTime: 10_000,
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  });
}

/** `POST /admissions/enquiries/{id}/stage-status:advance-if-complete`
 * — idempotent. Returns `{ advanced: false, ... }` when the engine
 * decided no_op (already past, or requirements not yet met); the FE
 * suppresses the success toast in that case. */
export function useAdvanceIfComplete(enquiryId: string) {
  const qc = useQueryClient();
  return useMutation<AdvanceIfCompleteResponse, Error, void>({
    mutationKey: [...admissionKeys.stageStatus(enquiryId), "advance-if-complete"],
    mutationFn: () =>
      api.post<AdvanceIfCompleteResponse>(
        `/admissions/enquiries/${enquiryId}/stage-status:advance-if-complete`,
        {},
      ),
    onSuccess: ({ enquiry }) => {
      qc.setQueryData(admissionKeys.enquiry(enquiryId), enquiry);
      void qc.invalidateQueries({ queryKey: admissionKeys.stageStatus(enquiryId) });
      void qc.invalidateQueries({ queryKey: admissionKeys.all });
    },
  });
}

/** `POST /admissions/enquiries/{id}/stage-status:force-advance` —
 * admin override path. Gated by `manage_admissions` AND
 * `admissions:override_stage_gate` server-side; the FE additionally
 * hides the trigger when `stageStatus.can_force_advance === false`
 * for defense-in-depth and a less-confusing read-only experience. */
export function useForceAdvance(enquiryId: string) {
  const qc = useQueryClient();
  return useMutation<ForceAdvanceResponse, Error, ForceAdvancePayload>({
    mutationKey: [...admissionKeys.stageStatus(enquiryId), "force-advance"],
    mutationFn: (payload) =>
      api.post<ForceAdvanceResponse>(
        `/admissions/enquiries/${enquiryId}/stage-status:force-advance`,
        payload,
      ),
    onSuccess: ({ enquiry }) => {
      qc.setQueryData(admissionKeys.enquiry(enquiryId), enquiry);
      void qc.invalidateQueries({ queryKey: admissionKeys.stageStatus(enquiryId) });
      void qc.invalidateQueries({ queryKey: admissionKeys.all });
    },
  });
}

/**
 * Phase H1 — `POST /admissions/enquiries/{id}/allow-next-stage`.
 *
 * Stamps `unlocked_stage_key` on the enquiry so the apply portal flips
 * from rendering the *current* stage's form to the *next* stage's
 * form for the parent. Single-use grant — consumed by the next
 * successful advance/force-advance/reject (the BE clears it
 * automatically on transition).
 *
 * UX contract surfaced by the StageProgressPanel:
 *   - CTA is hidden when the funnel says no next stage exists
 *     (terminal stage today or `unlocked_stage_key` already set).
 *   - On success the enquiry cache is rewritten in-place so the
 *     "Next stage allowed → <label>" chip shows up immediately.
 *   - The grant is single-use: subsequent transitions clear the
 *     stamp automatically server-side.
 *
 * Permission: `manage_admissions` server-side (BE refuses with 403
 * for read-only roles). Mirrored by the surrounding panel hiding
 * the CTA outside `manage_admissions`.
 */
export function useAllowNextStage(enquiryId: string) {
  const qc = useQueryClient();
  return useMutation<AllowNextStageResponse, Error, AllowNextStagePayload>({
    mutationKey: [...admissionKeys.enquiry(enquiryId), "allow-next-stage"],
    mutationFn: (payload) =>
      api.post<AllowNextStageResponse>(
        `/admissions/enquiries/${enquiryId}/allow-next-stage`,
        payload,
      ),
    onSuccess: ({ enquiry }) => {
      qc.setQueryData(admissionKeys.enquiry(enquiryId), enquiry);
      void qc.invalidateQueries({ queryKey: admissionKeys.stageStatus(enquiryId) });
      void qc.invalidateQueries({ queryKey: admissionKeys.all });
    },
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────

export function useCreateEnquiry() {
  const qc = useQueryClient();
  return useMutation<AdmissionEnquiry, Error, CreateEnquiryPayload>({
    mutationFn: (payload) => api.post<AdmissionEnquiry>("/admissions/enquiries", payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: admissionKeys.all });
    },
  });
}

export function useUpdateEnquiry(enquiryId: string) {
  const qc = useQueryClient();
  return useMutation<AdmissionEnquiry, Error, UpdateEnquiryPayload>({
    mutationFn: (payload) =>
      api.patch<AdmissionEnquiry>(`/admissions/enquiries/${enquiryId}`, payload),
    onSuccess: (data) => {
      // Optimistic-ish: prime the single-enquiry cache so the detail
      // page doesn't flicker while the list cache settles.
      qc.setQueryData(admissionKeys.enquiry(enquiryId), data);
      void qc.invalidateQueries({ queryKey: admissionKeys.all });
    },
  });
}

export function useSaveStagePayload(enquiryId: string) {
  const qc = useQueryClient();
  return useMutation<
    StagePayloadResponse,
    Error,
    { stageKey: string; payload: Record<string, unknown> }
  >({
    mutationFn: ({ stageKey, payload }) =>
      api.put<StagePayloadResponse>(
        `/admissions/enquiries/${enquiryId}/stage-payload/${stageKey}`,
        { payload },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: admissionKeys.enquiry(enquiryId) });
      void qc.invalidateQueries({
        queryKey: admissionKeys.activeForm(enquiryId),
      });
      void qc.invalidateQueries({ queryKey: admissionKeys.all });
    },
  });
}

export function useAdvanceEnquiry(enquiryId: string) {
  const qc = useQueryClient();
  return useMutation<AdmissionEnquiry, Error, AdvanceEnquiryPayload>({
    mutationFn: (payload) =>
      api.post<AdmissionEnquiry>(`/admissions/enquiries/${enquiryId}/advance`, payload),
    onSuccess: (data) => {
      qc.setQueryData(admissionKeys.enquiry(enquiryId), data);
      void qc.invalidateQueries({ queryKey: admissionKeys.all });
    },
  });
}

export function useRejectEnquiry(enquiryId: string) {
  const qc = useQueryClient();
  return useMutation<AdmissionEnquiry, Error, RejectEnquiryPayload>({
    mutationFn: (payload) =>
      api.post<AdmissionEnquiry>(`/admissions/enquiries/${enquiryId}/reject`, payload),
    onSuccess: (data) => {
      qc.setQueryData(admissionKeys.enquiry(enquiryId), data);
      void qc.invalidateQueries({ queryKey: admissionKeys.all });
    },
  });
}

/** Soft-delete (204). Returns void; cache invalidation pulls the row
 * out of the default list. The detail-page route will then 404 the
 * deleted enquiry — caller is responsible for navigating away. */
export function useDeleteEnquiry() {
  const qc = useQueryClient();
  return useMutation<void, Error, { enquiryId: string }>({
    mutationFn: async ({ enquiryId }) => {
      await api.delete<void>(`/admissions/enquiries/${enquiryId}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: admissionKeys.all });
    },
  });
}

export function useRestoreEnquiry(enquiryId: string) {
  const qc = useQueryClient();
  return useMutation<AdmissionEnquiry, Error, void>({
    mutationFn: () => api.post<AdmissionEnquiry>(`/admissions/enquiries/${enquiryId}/restore`, {}),
    onSuccess: (data) => {
      qc.setQueryData(admissionKeys.enquiry(enquiryId), data);
      void qc.invalidateQueries({ queryKey: admissionKeys.all });
    },
  });
}

export function useConvertEnquiry(enquiryId: string) {
  const qc = useQueryClient();
  return useMutation<AdmissionRecord, Error, ConvertEnquiryPayload>({
    mutationFn: (payload) =>
      api.post<AdmissionRecord>(`/admissions/enquiries/${enquiryId}/convert`, payload),
    onSuccess: () => {
      // Convert may return 200 (idempotent replay) or 201 (fresh).
      // Either way, both the enquiry (back-pointer fills) and the
      // converted-admissions list are stale.
      void qc.invalidateQueries({ queryKey: admissionKeys.all });
    },
  });
}

// ── Converted Admissions list ─────────────────────────────────────────────

export function useAdmissions(params?: {
  status?: string;
  branch_id?: string;
  limit?: number;
  offset?: number;
}) {
  const search = new URLSearchParams();
  if (params?.status) search.set("status", params.status);
  if (params?.branch_id) search.set("branch_id", params.branch_id);
  if (params?.limit !== undefined) search.set("limit", String(params.limit));
  if (params?.offset !== undefined) search.set("offset", String(params.offset));
  const qs = search.toString();
  return useQuery<AdmissionListResponse>({
    queryKey: admissionKeys.admissions(params),
    queryFn: () => api.get<AdmissionListResponse>(`/admissions/admissions${qs ? `?${qs}` : ""}`),
    staleTime: 30_000,
  });
}

export function useAdmission(admissionId: string | null) {
  return useQuery<AdmissionRecord>({
    queryKey: admissionKeys.admission(admissionId),
    queryFn: () => api.get<AdmissionRecord>(`/admissions/admissions/${admissionId}`),
    enabled: Boolean(admissionId),
    staleTime: 30_000,
  });
}

// ── Phase D3 — Follow-ups + Calendar ─────────────────────────────────────

/**
 * PATCH `/admissions/enquiries/{id}/followup` — set or clear a
 * scheduled follow-up. Auto-appends a `followup_set` activity entry
 * server-side (with `metadata.cleared=true` when the date is null).
 */
export function useSetFollowup(enquiryId: string) {
  const qc = useQueryClient();
  return useMutation<AdmissionEnquiry, Error, SetFollowupPayload>({
    mutationFn: (payload) =>
      api.patch<AdmissionEnquiry>(`/admissions/enquiries/${enquiryId}/followup`, payload),
    onSuccess: (data) => {
      qc.setQueryData(admissionKeys.enquiry(enquiryId), data);
      void qc.invalidateQueries({ queryKey: admissionKeys.all });
    },
  });
}

export function useCalendar(params?: { window_days?: number; include_overdue?: boolean }) {
  const search = new URLSearchParams();
  if (params?.window_days !== undefined) search.set("window_days", String(params.window_days));
  if (params?.include_overdue !== undefined) {
    search.set("include_overdue", params.include_overdue ? "true" : "false");
  }
  const qs = search.toString();
  return useQuery<CalendarResponse>({
    queryKey: admissionKeys.calendar(params),
    queryFn: () => api.get<CalendarResponse>(`/admissions/calendar${qs ? `?${qs}` : ""}`),
    // Calendar is a derived view — refresh more aggressively so a
    // freshly-set follow-up shows up without a manual reload.
    staleTime: 10_000,
  });
}

// ── Phase D5 — Assignment + collaborators + workload ─────────────────────

export function useAssignEnquiry(enquiryId: string) {
  const qc = useQueryClient();
  return useMutation<AdmissionEnquiry, Error, AssignEnquiryPayload>({
    mutationFn: (payload) =>
      api.post<AdmissionEnquiry>(`/admissions/enquiries/${enquiryId}/assign`, payload),
    onSuccess: (data) => {
      qc.setQueryData(admissionKeys.enquiry(enquiryId), data);
      void qc.invalidateQueries({ queryKey: admissionKeys.all });
    },
  });
}

export function useAddCollaborator(enquiryId: string) {
  const qc = useQueryClient();
  return useMutation<AdmissionEnquiry, Error, AddCollaboratorPayload>({
    mutationFn: (payload) =>
      api.post<AdmissionEnquiry>(`/admissions/enquiries/${enquiryId}/collaborators`, payload),
    onSuccess: (data) => {
      qc.setQueryData(admissionKeys.enquiry(enquiryId), data);
      void qc.invalidateQueries({ queryKey: admissionKeys.all });
    },
  });
}

export function useRemoveCollaborator(enquiryId: string) {
  const qc = useQueryClient();
  return useMutation<AdmissionEnquiry, Error, { user_id: string }>({
    mutationFn: ({ user_id }) =>
      api.delete<AdmissionEnquiry>(`/admissions/enquiries/${enquiryId}/collaborators/${user_id}`),
    onSuccess: (data) => {
      qc.setQueryData(admissionKeys.enquiry(enquiryId), data);
      void qc.invalidateQueries({ queryKey: admissionKeys.all });
    },
  });
}

export function useWorkload() {
  return useQuery<WorkloadResponse>({
    queryKey: admissionKeys.workload(),
    queryFn: () => api.get<WorkloadResponse>("/admissions/workload"),
    staleTime: 30_000,
  });
}

/**
 * D5e — Active assignable staff for the AssignmentMenu picker.
 *
 * Cached for 60s because the roster only changes when staff are
 * invited / suspended, and the IA opens the menu many times per
 * session — re-fetching on every open would feel sluggish without
 * adding any safety.
 */
export function useStaffRoster() {
  return useQuery<StaffRosterResponse>({
    queryKey: admissionKeys.staff(),
    queryFn: () => api.get<StaffRosterResponse>("/admissions/staff"),
    staleTime: 60_000,
  });
}

// ── Phase D6 — CRM Activity log ──────────────────────────────────────────

export function useAddActivity(enquiryId: string) {
  const qc = useQueryClient();
  return useMutation<AdmissionEnquiry, Error, AddActivityPayload>({
    mutationFn: (payload) =>
      api.post<AdmissionEnquiry>(`/admissions/enquiries/${enquiryId}/activities`, payload),
    onSuccess: (data) => {
      qc.setQueryData(admissionKeys.enquiry(enquiryId), data);
      void qc.invalidateQueries({ queryKey: admissionKeys.all });
    },
  });
}

// ── Phase D4 — Bulk operations ───────────────────────────────────────────

/**
 * Bulk endpoints always return HTTP 200 with a per-item success/failure
 * envelope. Caller decides how to surface partial failures (typically
 * a toast + a "review failed" affordance).
 */
export function useBulkAdvance() {
  const qc = useQueryClient();
  return useMutation<BulkResultResponse, Error, BulkAdvancePayload>({
    mutationFn: (payload) =>
      api.post<BulkResultResponse>("/admissions/enquiries/bulk-advance", payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: admissionKeys.all });
    },
  });
}

export function useBulkAssign() {
  const qc = useQueryClient();
  return useMutation<BulkResultResponse, Error, BulkAssignPayload>({
    mutationFn: (payload) =>
      api.post<BulkResultResponse>("/admissions/enquiries/bulk-assign", payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: admissionKeys.all });
    },
  });
}

export function useBulkDelete() {
  const qc = useQueryClient();
  return useMutation<BulkResultResponse, Error, BulkDeletePayload>({
    mutationFn: (payload) =>
      api.post<BulkResultResponse>("/admissions/enquiries/bulk-delete", payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: admissionKeys.all });
    },
  });
}

/**
 * CSV export — returns a Blob URL the caller hands to a synthetic
 * `<a download>` click. We intentionally bypass the JSON pipeline
 * (axios `responseType: blob`) so the file is streamed straight to
 * disk instead of buffered as a string.
 */
export async function exportEnquiriesCsv(filters?: EnquiryListFilters): Promise<Blob> {
  const search = new URLSearchParams();
  if (filters?.stage_key) search.set("stage_key", filters.stage_key);
  if (filters?.assigned_to) search.set("assigned_to", filters.assigned_to);
  if (filters?.branch_id) search.set("branch_id", filters.branch_id);
  if (filters?.source) search.set("source", filters.source);
  if (filters?.q) search.set("q", filters.q);
  if (filters?.followup_filter) search.set("followup_filter", filters.followup_filter);
  const qs = search.toString();
  const resp = await api.raw.get<Blob>(`/admissions/enquiries/export.csv${qs ? `?${qs}` : ""}`, {
    responseType: "blob",
  });
  return resp.data;
}

// ─────────────────────────────────────────────────────────────────────────
// Phase F11 FE-3 — Proctor review surface (BE-2 endpoints)
// ─────────────────────────────────────────────────────────────────────────

/** Cache key shape for the proctor review hooks. Centralised so a
 * single ``invalidateQueries({ queryKey: proctorKeys.all })`` busts
 * the entire surface (used after IA-side mutations like marking an
 * artifact as reviewed land in a future phase). */
export const proctorKeys = {
  all: ["proctor"] as const,
  attemptsForEnquiry: (enquiryId: string | null) =>
    [...proctorKeys.all, "attempts-for-enquiry", enquiryId ?? "_none"] as const,
  artifacts: (attemptId: string | null, kind: ReviewProctorArtifactKind | "all") =>
    [...proctorKeys.all, "artifacts", attemptId ?? "_none", kind] as const,
  events: (attemptId: string | null, kind: ReviewProctorEventKind | "all") =>
    [...proctorKeys.all, "events", attemptId ?? "_none", kind] as const,
  cheatAssessment: (attemptId: string | null) =>
    [...proctorKeys.all, "cheat-assessment", attemptId ?? "_none"] as const,
};

/**
 * List every assessment attempt belonging to one enquiry. Powers the
 * IA enquiry detail page's "Proctoring" section — each attempt with
 * a non-``off`` proctor profile gets a per-attempt review tab.
 */
export function useEnquiryAttempts(enquiryId: string | null) {
  return useQuery({
    queryKey: proctorKeys.attemptsForEnquiry(enquiryId),
    queryFn: () =>
      api.get<AttemptListResponse>(
        `/assessments/attempts?enquiry_id=${encodeURIComponent(enquiryId ?? "")}`,
      ),
    enabled: Boolean(enquiryId),
    staleTime: 30_000,
  });
}

/**
 * List proctor artifacts for one attempt. ``kind === "all"`` skips the
 * filter parameter; passing a specific kind narrows to one tab in the
 * gallery (e.g. ``id_document``, ``webcam_still``, ``screen_capture``).
 *
 * The list is cheap: BE returns metadata only, no signed URLs. The
 * IA UI calls :func:`useProctorDownloadUrl` lazily on click for
 * preview-on-demand.
 */
export function useProctorArtifacts(
  attemptId: string | null,
  kind: ReviewProctorArtifactKind | "all" = "all",
) {
  return useQuery({
    queryKey: proctorKeys.artifacts(attemptId, kind),
    queryFn: () => {
      const qs = kind === "all" ? "" : `?kind=${encodeURIComponent(kind)}`;
      return api.get<ReviewProctorArtifactList>(
        `/assessments/attempts/${attemptId}/proctor-artifacts${qs}`,
      );
    },
    enabled: Boolean(attemptId),
    staleTime: 30_000,
  });
}

/**
 * On-demand mutation that mints a short-lived signed GET URL for one
 * artifact. We use a mutation (not a query) so the URL is requested
 * lazily on user gesture (clicking a thumbnail) rather than
 * speculatively for every row in the gallery — minting URLs is
 * cheap but the GCS PUT side already incurs per-call cost we don't
 * want to amplify on a paginated gallery.
 */
export function useMintProctorDownloadUrl() {
  return useMutation({
    mutationFn: async (params: { attemptId: string; artifactId: string }) =>
      api.get<ReviewProctorDownloadUrl>(
        `/assessments/attempts/${params.attemptId}/proctor-artifacts/${params.artifactId}/download-url`,
      ),
  });
}

/** List proctor events (consent, tab blur, paste blocks, etc.) for
 * one attempt. ``kind === "all"`` returns everything in chronological
 * order; a kind filter narrows to the corresponding subset. */
export function useProctorEvents(
  attemptId: string | null,
  kind: ReviewProctorEventKind | "all" = "all",
) {
  return useQuery({
    queryKey: proctorKeys.events(attemptId, kind),
    queryFn: () => {
      const qs = kind === "all" ? "" : `?kind=${encodeURIComponent(kind)}`;
      return api.get<ReviewProctorEventList>(
        `/assessments/attempts/${attemptId}/proctor-events${qs}`,
      );
    },
    enabled: Boolean(attemptId),
    staleTime: 30_000,
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Phase F12 BE-1 — Deterministic cheat-detection scorer (admin)
// ─────────────────────────────────────────────────────────────────────────

/**
 * Fetch the deterministic cheat-detection assessment for one attempt.
 *
 * The BE returns 404 with ``code === "CHEAT_ASSESSMENT_NOT_FOUND"`` for:
 *   * Pre-F12 attempts that submitted before the scorer landed.
 *   * Attempts that haven't been submitted yet (the scorer runs in
 *     :func:`submit_attempt` after grading).
 *   * Attempts whose blob failed to validate against the current
 *     scorer schema (this should be exceedingly rare; recompute fixes
 *     it).
 *
 * We treat the 404 as a non-error empty state — ``data`` is ``null``
 * and ``isLoading`` is ``false`` once the query settles. Any other
 * status (network, 500, 403) flows through ``error`` as usual so the
 * UI can render a destructive banner.
 */
export function useCheatAssessment(attemptId: string | null) {
  return useQuery<CheatAssessment | null>({
    queryKey: proctorKeys.cheatAssessment(attemptId),
    queryFn: async () => {
      try {
        return await api.get<CheatAssessment>(
          `/assessments/attempts/${encodeURIComponent(attemptId ?? "")}/cheat-assessment`,
        );
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          return null;
        }
        throw err;
      }
    },
    enabled: Boolean(attemptId),
    staleTime: 30_000,
  });
}

/**
 * Recompute the cheat-detection score for one attempt.
 *
 * Reviewers use this when:
 *   * New proctor artifacts have arrived since the original
 *     auto-compute (e.g. uploads that failed initially are retried
 *     and now land as ``failed`` artifacts the scorer should weigh).
 *   * The reviewer suspects the original scoring used stale state
 *     (network blip during submit, partial event flush).
 *
 * The BE audit-logs every recompute with the supplied ``reason``
 * (4–500 chars) alongside the calling reviewer id. ``reason`` is
 * NOT free-form internal notes — surface this to the user as
 * "Why are you recomputing?" so the audit trail is meaningful.
 *
 * Important: this does NOT re-run the F10b outcome resolver. If the
 * recompute flips ``proctor_demotion`` and the reviewer wants the
 * outcome refreshed too, they call the separate
 * ``:recompute-outcome`` endpoint (out of scope for F12 FE-1).
 */
export function useRecomputeCheatAssessment(attemptId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CheatAssessmentRecomputeRequest) =>
      api.post<CheatAssessment>(
        `/assessments/attempts/${encodeURIComponent(attemptId ?? "")}/cheat-assessment:recompute`,
        body,
      ),
    onSuccess: (data) => {
      // Replace the cached value in-place so the UI flips from
      // "stale" to "fresh" without a refetch round-trip. Subsequent
      // navigations will refetch when the staleTime elapses.
      qc.setQueryData(proctorKeys.cheatAssessment(attemptId), data);
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Phase F8b BE-1 — Admin grant-reattempt slice
// ─────────────────────────────────────────────────────────────────────────

/**
 * Mutation hook for ``POST /assessments/attempts/{attemptId}:grant-reattempt``.
 *
 * Atomically invalidates the supplied attempt (its status flips to
 * ``withdrawn`` with ``withdrawn_reason`` prefixed
 * ``reattempt_granted: <reason>``) and assigns a fresh attempt for
 * the same (enquiry, template) pair, bypassing the template's
 * ``max_attempts`` cap.
 *
 * The BE refuses with HTTP 409 when the attempt is in a terminal
 * state (``submitted`` / ``graded`` / ``expired`` / ``withdrawn``) —
 * the dialog surfaces the API error code so the IA understands why
 * the CTA is disabled or the request was rejected. Callers must
 * additionally validate the reason length client-side before
 * dispatching (the BE enforces 1–500 chars and 422s otherwise).
 *
 * On success we invalidate every proctor + enquiry-attempt cache so
 * the detail panel re-renders with the fresh tab list (the new
 * attempt) and the invalidated attempt's status badge flips to
 * ``withdrawn``.
 */
export function useGrantReattempt(attemptId: string | null, enquiryId: string | null) {
  const qc = useQueryClient();
  return useMutation<GrantReattemptResponse, Error, GrantReattemptRequest>({
    mutationFn: (body) =>
      api.post<GrantReattemptResponse>(
        `/assessments/attempts/${encodeURIComponent(attemptId ?? "")}:grant-reattempt`,
        body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: proctorKeys.all });
      if (enquiryId) {
        void qc.invalidateQueries({ queryKey: admissionKeys.enquiry(enquiryId) });
      }
    },
  });
}
